/**
 * @todo Maybe make this class a util class, or migrate these into
 * the line class? The future orthogonal connector will be very
 * different, and only use a few methods below.
 * It's currently just providing util methods and a consistent
 * constructor API. Audit the differences between way vertexes are
 * expected to instantiate, and whether line could be a "vertex."
 */

/**
 * @param {fabric.Canvas} canvas - the fabric canvas
 * @param {Object} config - edge configuration
 * @param {'line'|'orthogonal'} config.edgeType - The type of edge being added
 */
export class BlueprintEdge {
  constructor(canvas, { edgeType }) {
    // Shared, init
    this.canvas = canvas;
    this.edgeType = edgeType;

    // Implemented only by children
    /** @todo: If this is built well, ref should be private */
    this.ref = null;

    this.snapThreshold = 10;
  }

  render() {
    this.ref.set('dirty', true);
    this.canvas.requestRenderAll();
  }

  getRadians = deg => deg * (Math.PI / 180);

  getAngleFromPoints = (a, b) =>
    Math.atan2(a.y - b.y, a.x - b.x) * (180 / Math.PI);

  // Function to check distance between two points
  getPointDistance = (a, b) => {
    const distX = a.x - b.x;
    const distY = a.y - b.y;

    return Math.sqrt(distX * distX + distY * distY);
  };

  // Find the middle of two given points
  getMidPoint = (a, b) => ({
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  });

  getArcMidPoint = (center, radiusX, radiusY, startAngle, endAngle) => {
    // Convert angles to radians
    const startAngleRad = this.getRadians(startAngle);
    const endAngleRad = this.getRadians(endAngle);

    // Calculate the angle of the midpoint of the arc
    const midAngleRad = (startAngleRad + endAngleRad) / 2;

    // Calculate the x and y coordinates of the midpoint of the arc
    const x = center.x + radiusX * Math.cos(midAngleRad);
    const y = center.y + radiusY * Math.sin(midAngleRad);

    return { x, y };
  };

  getVertexSnappingPoints = vertex => {
    // Ensure object coordinates are up to date
    vertex.setCoords();

    // Get the corner coordinates
    const { tl: actl, tr: actr, br: acbr, bl: acbl } = vertex.aCoords;

    // top center
    const tc = {
      x: (actl.x + actr.x) / 2,
      y: (actl.y + actr.y) / 2,
    };
    // bottom center
    const bc = {
      x: (acbl.x + acbr.x) / 2,
      y: (acbl.y + acbr.y) / 2,
    };
    // left center
    const lc = {
      x: (actl.x + acbl.x) / 2,
      y: (actl.y + acbl.y) / 2,
    };
    // right center
    const rc = {
      x: (actr.x + acbr.x) / 2,
      y: (actr.y + acbr.y) / 2,
    };

    // Return an array of points, starting from the top-left
    switch (vertex.type) {
      /** @todo use shapetypes? */
      case 'triangle': {
        const tlc = this.getMidPoint(acbl, tc);
        const trc = this.getMidPoint(acbr, tc);

        return [tc, trc, acbr, bc, acbl, tlc];
      }
      case 'rect':
        return [actl, tc, actr, rc, acbr, bc, acbl, lc];
      case 'circle': {
        const centerPoint = vertex.getCenterPoint();
        const { radius: baseRadius } = vertex;
        const radiusX = baseRadius * vertex.scaleX;
        const radiusY = baseRadius * vertex.scaleY;

        // in a cartesian system "quadrant 1" is in the top right, 90 degrees would have a vertical line (top center) because it's relative to the positive x-axis (right center), 180 would be the extension into the negative x-asis (left center)
        const ctl = this.getArcMidPoint(centerPoint, radiusX, radiusY, 90, 180);
        const ctr = this.getArcMidPoint(centerPoint, radiusX, radiusY, 0, 90);
        const cbr = this.getArcMidPoint(
          centerPoint,
          radiusX,
          radiusY,
          270,
          360,
        );
        const cbl = this.getArcMidPoint(
          centerPoint,
          radiusX,
          radiusY,
          180,
          270,
        );
        return [ctl, tc, ctr, rc, cbr, bc, cbl, lc];
      }
      case 'polygon': {
        // diamond
        const dtl = this.getMidPoint(lc, tc);
        const dtr = this.getMidPoint(tc, rc);
        const dbr = this.getMidPoint(rc, bc);
        const dbl = this.getMidPoint(bc, lc);
        return [dtl, tc, dtr, rc, dbr, bc, dbl, lc];
      }
      default:
        return [];
    }
  };

  /**
   * Center the shape (by moving it) in the current viewport
   *  See @todo at top of file.
   */
  // centerInViewport() {}

  /**
   * Get the center position
   * This must be an instance method because getters/setters aren't inherited in JS
   * and it only makes sense for straight lines. See @todo at top of file.
   * @returns {{ x: number, y: number }} point
   */
  // getCenterPoint() {}

  /**
   * Position the line by it's center
   * This must be an instance method because getters/setters aren't inherited in JS
   * and it only makes sense for straight lines. See @todo at top of file.
   * @param {{ x: number, y: number }} point
   */
  // setCenterPoint(point) {}

  /** override on instance */
  clone() {
    return new BlueprintEdge(this.canvas, this.edgeType);
  }
}
